import { SEO_PAGES, type PageSEOMeta } from './seo-config';

/**
 * SEO authoring rules, enforced at build time by scripts/prerender.mjs.
 *   title        50–60 characters
 *   description  140–158 characters
 * Every title and description must also be unique across the site.
 */
export const TITLE_MIN = 50;
export const TITLE_MAX = 60;
export const DESC_MIN = 140;
export const DESC_MAX = 158;

export interface SeoIssue {
  path: string;
  field: 'title' | 'description';
  problem: string;
  length: number;
  value: string;
}

export interface SeoAuditResult {
  issues: SeoIssue[];
  titleCount: number;
  descCount: number;
  titlesOutOfRange: number;
  descsOutOfRange: number;
  duplicateTitles: string[];
  duplicateDescriptions: string[];
}

export interface SeoEntry {
  path: string;
  title?: string;
  description?: string;
}

/**
 * Audits an explicit list of page→meta entries. Used for the static map via
 * `auditSeoMap`, and for the DYNAMIC routes (careers / insights / resources)
 * via the prerenderer, so out-of-range or duplicate dynamic metadata fails the
 * build exactly like the static pages.
 */
export function auditSeoEntries(entries: SeoEntry[]): SeoAuditResult {
  const issues: SeoIssue[] = [];
  const titleSeen = new Map<string, string[]>();
  const descSeen = new Map<string, string[]>();
  let titlesOutOfRange = 0;
  let descsOutOfRange = 0;
  let titleCount = 0;
  let descCount = 0;

  entries.forEach(({ path, title: rawTitle, description: rawDesc }) => {
    const title = (rawTitle || '').trim();
    const desc = (rawDesc || '').trim();

    if (title) {
      titleCount += 1;
      if (title.length < TITLE_MIN || title.length > TITLE_MAX) {
        titlesOutOfRange += 1;
        issues.push({
          path,
          field: 'title',
          problem: `length ${title.length} outside ${TITLE_MIN}–${TITLE_MAX}`,
          length: title.length,
          value: title,
        });
      }
      const k = norm(title);
      titleSeen.set(k, [...(titleSeen.get(k) || []), path]);
    }

    if (desc) {
      descCount += 1;
      if (desc.length < DESC_MIN || desc.length > DESC_MAX) {
        descsOutOfRange += 1;
        issues.push({
          path,
          field: 'description',
          problem: `length ${desc.length} outside ${DESC_MIN}–${DESC_MAX}`,
          length: desc.length,
          value: desc,
        });
      }
      const k = norm(desc);
      descSeen.set(k, [...(descSeen.get(k) || []), path]);
    }
  });

  const duplicateTitles: string[] = [];
  titleSeen.forEach((paths, key) => {
    if (paths.length > 1) {
      duplicateTitles.push(key);
      paths.forEach((p) =>
        issues.push({ path: p, field: 'title', problem: `duplicate title (${paths.length}×)`, length: key.length, value: key }),
      );
    }
  });

  const duplicateDescriptions: string[] = [];
  descSeen.forEach((paths, key) => {
    if (paths.length > 1) {
      duplicateDescriptions.push(key);
      paths.forEach((p) =>
        issues.push({ path: p, field: 'description', problem: `duplicate description (${paths.length}×)`, length: key.length, value: key }),
      );
    }
  });

  return {
    issues,
    titleCount,
    descCount,
    titlesOutOfRange,
    descsOutOfRange,
    duplicateTitles,
    duplicateDescriptions,
  };
}

/** Normalises a value for duplicate comparison. */
function norm(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Audits a page→meta map: range checks for every title/description plus
 * duplicate detection. Returns the full issue list so callers can log and/or
 * fail the build.
 */
export function auditSeoMap(map: Record<string, PageSEOMeta> = SEO_PAGES): SeoAuditResult {
  return auditSeoEntries(
    Object.entries(map).map(([path, meta]) => ({
      path,
      title: meta.title,
      description: meta.description,
    })),
  );
}

/** Counts <h1> elements in an HTML string (crawl-time H1-per-page check). */
export function countH1(html: string): number {
  return (html.match(/<h1[\s>]/gi) || []).length;
}